import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

/**
 * The deployment operator's read-only view across organizations. Metadata and numbers only:
 * nothing here may carry message, memory, file, secret or credential content.
 */

export const PLATFORM_ADMIN_PAGE_SIZE = 50;

/** Derived from the organization's billing account; `none` when it never had one. */
export const PlatformBillingStateSchema = z.enum([
  "trial",
  "active",
  "past_due",
  "canceled",
  "none",
]);
export type PlatformBillingState = z.infer<typeof PlatformBillingStateSchema>;

export const PlatformBillingSchema = z.object({
  state: PlatformBillingStateSchema,
  /** Whole days left while in trial, never negative; null otherwise. */
  trialDaysLeft: z.number().int().nonnegative().nullable(),
});
export type PlatformBilling = z.infer<typeof PlatformBillingSchema>;

/** How far a new organization got: no bot yet, a bot that never ran, or at least one run. */
export const PlatformOnboardingSchema = z.enum(["no_bot", "no_run", "running"]);
export type PlatformOnboarding = z.infer<typeof PlatformOnboardingSchema>;

export const PLATFORM_COMPANY_SORTS = [
  "name",
  "createdAt",
  "members",
  "bots",
  "spend7d",
  "spend30d",
  "lastActive",
] as const;
export const PlatformCompanySortSchema = z.enum(PLATFORM_COMPANY_SORTS);
export type PlatformCompanySort = z.infer<typeof PlatformCompanySortSchema>;

export const PlatformCompaniesInputSchema = z.object({
  /** Matches the organization's name or slug, or its owner's email. */
  query: z.string().trim().max(200).optional(),
  sort: PlatformCompanySortSchema.default("createdAt"),
  direction: z.enum(["asc", "desc"]).default("desc"),
  page: z.number().int().min(0).max(100_000).default(0),
});
export type PlatformCompaniesInput = z.input<typeof PlatformCompaniesInputSchema>;

export const PlatformCompanySchema = z.object({
  organizationId: Id,
  name: z.string(),
  createdAt: IsoDate,
  ownerEmail: z.string().nullable(),
  members: z.number().int().nonnegative(),
  spaces: z.number().int().nonnegative(),
  /** Bots that spent anything in the last 7 days. */
  activeBots: z.number().int().nonnegative(),
  /** Bots not archived. */
  bots: z.number().int().nonnegative(),
  billing: PlatformBillingSchema,
  onboarding: PlatformOnboardingSchema,
  spend7dUsd: z.number().nonnegative(),
  spend30dUsd: z.number().nonnegative(),
  /** The latest session activity of any member. */
  lastActiveAt: IsoDate.nullable(),
});
export type PlatformCompany = z.infer<typeof PlatformCompanySchema>;

export const PlatformCompaniesSchema = z.object({
  companies: z.array(PlatformCompanySchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  pageSize: z.number().int().positive(),
});
export type PlatformCompanies = z.infer<typeof PlatformCompaniesSchema>;

export const PlatformOverviewSchema = z.object({
  companies: z.number().int().nonnegative(),
  newThisWeek: z.number().int().nonnegative(),
  inTrial: z.number().int().nonnegative(),
  paying: z.number().int().nonnegative(),
  spend30dUsd: z.number().nonnegative(),
  topBySpend: z.array(
    z.object({ organizationId: Id, name: z.string(), spend30dUsd: z.number().nonnegative() }),
  ),
});
export type PlatformOverview = z.infer<typeof PlatformOverviewSchema>;

export const PlatformBotStatusSchema = z.enum(["working", "idle", "archived"]);
export type PlatformBotStatus = z.infer<typeof PlatformBotStatusSchema>;

export const PlatformCompanyBotSchema = z.object({
  botId: Id,
  name: z.string(),
  spaceId: Id,
  /** Null runs the Space or deployment default. */
  model: z.string().nullable(),
  runs7d: z.number().int().nonnegative(),
  runs30d: z.number().int().nonnegative(),
  failedRuns7d: z.number().int().nonnegative(),
  spend7dUsd: z.number().nonnegative(),
  spend30dUsd: z.number().nonnegative(),
  /** The cap that applies now: the bot's own, else its Space default; null means none. */
  dailyCapUsd: z.number().nonnegative().nullable(),
  /** Days in the last 30, in the owner's time zone, whose spend reached today's cap. */
  daysAtCap30d: z.number().int().nonnegative(),
  lastRunAt: IsoDate.nullable(),
  status: PlatformBotStatusSchema,
});
export type PlatformCompanyBot = z.infer<typeof PlatformCompanyBotSchema>;

export const PlatformIntegrationSchema = z.object({
  kind: z.enum(["app", "mcp", "messaging"]),
  name: z.string(),
  needsReconnect: z.boolean(),
});
export type PlatformIntegration = z.infer<typeof PlatformIntegrationSchema>;

export const PlatformCompanyDetailSchema = z.object({
  company: PlatformCompanySchema,
  spaces: z.array(z.object({ spaceId: Id, name: z.string(), createdAt: IsoDate })),
  bots: z.array(PlatformCompanyBotSchema),
  /** Spend per UTC day and operation kind over the last 30 days; days without spend are absent. */
  usageByDay: z.array(
    z.object({ day: z.string(), kind: z.string(), spendUsd: z.number().nonnegative() }),
  ),
  integrations: z.array(PlatformIntegrationSchema),
  members: z.array(
    z.object({
      email: z.string(),
      role: z.string(),
      joinedAt: IsoDate,
      lastSeenAt: IsoDate.nullable(),
    }),
  ),
  failedRuns: z.object({
    last24h: z.number().int().nonnegative(),
    last7d: z.number().int().nonnegative(),
  }),
});
export type PlatformCompanyDetail = z.infer<typeof PlatformCompanyDetailSchema>;

/** The roles the deployment can include a model for, so new companies need no key of their own. */
export const IncludedModelRoleSchema = z.enum([
  "main",
  "approval",
  "image",
  "video",
  "documents",
  "voice",
]);
export type IncludedModelRole = z.infer<typeof IncludedModelRoleSchema>;
export const IncludedConnectionKindSchema = z.enum(["model", "voice"]);
export type IncludedConnectionKind = z.infer<typeof IncludedConnectionKindSchema>;

/**
 * The deployment's included models as platform admins see them. Keys are write-only: a
 * connection reports only that it exists.
 */
export const IncludedModelsSchema = z.object({
  connections: z.array(
    z.object({ kind: IncludedConnectionKindSchema, provider: z.string(), updatedAt: IsoDate }),
  ),
  roles: z.array(
    z.object({
      role: IncludedModelRoleSchema,
      provider: z.string(),
      modelId: z.string(),
      speechModel: z.string().nullable(),
    }),
  ),
  /** What each role can include: chat models for main and approval, media catalogs otherwise. */
  options: z.array(
    z.object({
      role: IncludedModelRoleSchema,
      provider: z.string(),
      providerName: z.string(),
      modelId: z.string(),
      label: z.string(),
    }),
  ),
});
export type IncludedModels = z.infer<typeof IncludedModelsSchema>;

export const IncludedConnectInputSchema = z.object({
  kind: IncludedConnectionKindSchema,
  provider: z.string().min(1).max(100),
  apiKey: z.string().trim().min(1).max(4096),
});
export const IncludedDisconnectInputSchema = z.object({
  kind: IncludedConnectionKindSchema,
  provider: z.string().min(1).max(100),
});
export const IncludedRoleInputSchema = z.object({
  role: IncludedModelRoleSchema,
  /** Null stops including a model for the role. For voice, `modelId` is the speech model. */
  choice: z
    .object({ provider: z.string().min(1).max(100), modelId: z.string().min(1).max(200) })
    .nullable(),
});
